// FilePick and useFilePicker: a file chooser behind a kit key. The input is hidden and the key
// opens it, so the trigger is whatever Button the surface needs: the big gold "Take a photo" on
// a phone, a tonal "Upload invoice" in a header. `capture` opens the camera straight away on a
// phone and is ignored on a desktop. useFilePicker is the same input with no key of its own, for
// a picker several triggers share. docs/operations/design-standard.md, "Controls".

import React, { useCallback, useRef } from 'react';
import { Button, ButtonProps } from './Button';

type PickerInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'accept' | 'multiple' | 'capture' | 'disabled' | 'onChange' | 'className' | 'children' | 'hidden'
> & { 'data-testid'?: string };

export interface FilePickerOptions {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  /** Opens the camera on a phone: 'environment' is the back one. A desktop shows its file picker. */
  capture?: 'environment' | 'user';
  /** Chooses a whole folder; every file inside it arrives, whatever `accept` says. */
  folder?: boolean;
  disabled?: boolean;
  inputProps?: PickerInputProps;
}

export interface FilePicker {
  /** Opens the picker. Call it from a click, or the browser refuses. */
  open: () => void;
  /** The hidden input. Render it once, anywhere in the component. */
  input: React.ReactElement;
  ref: React.RefObject<HTMLInputElement>;
}

export function useFilePicker({ onFiles, accept, multiple, capture, folder, disabled, inputProps = {} }: FilePickerOptions): FilePicker {
  const ref = useRef<HTMLInputElement>(null);
  const latest = useRef(onFiles);
  latest.current = onFiles;

  const open = useCallback(() => { ref.current?.click(); }, []);

  const input = (
    <input
      {...inputProps}
      ref={ref}
      type="file"
      accept={accept}
      multiple={multiple}
      capture={capture}
      disabled={disabled}
      // webkitdirectory is not in React's input typings.
      {...((folder ? { webkitdirectory: '' } : {}) as object)}
      className="hidden"
      onChange={(e) => {
        // Copied before the reset: clearing the value lets the same file be chosen again.
        const files = Array.from(e.currentTarget.files || []);
        e.currentTarget.value = '';
        if (files.length) latest.current(files);
      }}
    />
  );

  return { open, input, ref };
}

export interface FilePickProps extends Omit<ButtonProps, 'onClick' | 'type' | 'onChange'>, Omit<FilePickerOptions, 'disabled'> {}

/** A key that opens a file picker. Any Button variant and size; the words say what is chosen. */
export const FilePick = React.forwardRef<HTMLButtonElement, FilePickProps>(
  ({ onFiles, accept, multiple, capture, folder, inputProps, disabled, children, ...button }, ref) => {
    const picker = useFilePicker({ onFiles, accept, multiple, capture, folder, disabled, inputProps });
    return (
      <>
        {picker.input}
        <Button ref={ref} {...button} disabled={disabled} onClick={picker.open}>
          {children}
        </Button>
      </>
    );
  },
);
FilePick.displayName = 'FilePick';

export default FilePick;
